import { useEffect, useState } from "react";
import { Package, Plus, Trash2, Pencil, TriangleAlert } from "lucide-react";
import { api, num, trDate } from "../lib/api";
import { PageHeader, Modal, FormField, SubmitButton, useForm, EmptyState, StatMini } from "../components/farm/FarmUI";
import { HERO, HERO_TINT } from "../lib/heroes";

export default function Stock() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { form, set, setForm, reset } = useForm({ name: "", category: "", quantity: "", unit: "adet", min_quantity: "", note: "" });

  const load = () => api.get("/stock").then(setItems).catch(() => setError("Yüklenemedi."));
  useEffect(() => { load(); }, []);

  const lowCount = items.filter((s) => Number(s.min_quantity) > 0 && Number(s.quantity) <= Number(s.min_quantity)).length;

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const updated = await api.patch(`/stock/${editing.id}`, {
          quantity: Number(form.quantity) || 0,
          min_quantity: Number(form.min_quantity) || 0,
          note: form.note,
        });
        setItems((x) => x.map((i) => (i.id === editing.id ? updated : i)));
      } else {
        const rec = await api.post("/stock", {
          name: form.name,
          category: form.category,
          quantity: Number(form.quantity) || 0,
          unit: form.unit || "adet",
          min_quantity: Number(form.min_quantity) || 0,
          note: form.note,
        });
        setItems((x) => [...x, rec].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      }
      reset();
      setEditing(null);
      setOpen(false);
    } catch { setError("Kaydedilemedi."); }
  };

  const remove = async (id) => {
    try { await api.del(`/stock/${id}`); setItems((x) => x.filter((i) => i.id !== id)); }
    catch { setError("Silinemedi."); }
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name, category: item.category || "",
      quantity: item.quantity, unit: item.unit,
      min_quantity: item.min_quantity, note: item.note || "",
    });
    setOpen(true);
  };

  const openNew = () => { reset(); setEditing(null); setOpen(true); };

  return (
    <>
      <PageHeader
        eyebrow="STOK YÖNETİMİ"
        title="Stoklar"
        subtitle="Gübre, yem, tohum ve tüm çiftlik stoklarınız."
        heroImage={HERO.stock}
        heroTint={HERO_TINT.stock}
        actions={
          <button className="primary-btn" data-testid="stock-add-btn" onClick={openNew}>
            <Plus size={17} /> Stok ekle
          </button>
        }
      />

      {error && <div className="api-error" data-testid="stock-error">{error}</div>}

      <section className="stats-mini-row">
        <StatMini label="Kalem sayısı" value={`${items.length}`} tone="teal" testId="stock-stat-count" />
        <StatMini label="Düşük stok" value={`${lowCount}`} tone={lowCount ? "red" : "green"} testId="stock-stat-low" />
      </section>

      <div className="panel">
        {items.length === 0 ? (
          <EmptyState icon={Package} title="Henüz stok yok" text="Gübre, yem, tohum, ilaç gibi tüm stokları buradan yönetin." actionLabel="İlk stoğu ekle" onAction={openNew} testId="stock-empty-add" />
        ) : (
          <div className="stock-grid">
            {items.map((s) => {
              const low = Number(s.min_quantity) > 0 && Number(s.quantity) <= Number(s.min_quantity);
              return (
                <div className={`stock-card ${low ? "is-low" : ""}`} key={s.id} data-testid={`stock-item-${s.id}`}>
                  <div className="stock-top">
                    <Package size={18} />
                    <div className="stock-info">
                      <b>{s.name}</b>
                      <small>{s.category || "Genel"} · Son güncelleme {trDate(s.updated_at)}</small>
                    </div>
                    {low && <span className="stock-warn" data-testid={`stock-warn-${s.id}`}><TriangleAlert size={12} /> Düşük</span>}
                  </div>
                  <div className="stock-metric">
                    <b>{num(s.quantity)} <small>{s.unit}</small></b>
                    <span>Min: {num(s.min_quantity)} {s.unit}</span>
                  </div>
                  {s.note && <p className="stock-note">{s.note}</p>}
                  <div className="stock-actions">
                    <button className="ghost-btn small" data-testid={`stock-edit-${s.id}`} onClick={() => openEdit(s)}>
                      <Pencil size={13} /> Güncelle
                    </button>
                    <button className="delete-btn always" data-testid={`stock-delete-${s.id}`} onClick={() => remove(s.id)}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {open && (
        <Modal title={editing ? "Stoğu güncelle" : "Yeni stok"} subtitle={editing ? "GÜNCELLE" : "STOK"} onClose={() => setOpen(false)}>
          <form onSubmit={submit}>
            {!editing && (
              <>
                <FormField label="Stok adı">
                  <input required data-testid="stock-form-name" placeholder="Örn. Üre gübresi" value={form.name} onChange={(e) => set("name", e.target.value)} />
                </FormField>
                <div className="form-row">
                  <FormField label="Kategori">
                    <input data-testid="stock-form-category" placeholder="Gübre, yem, tohum..." value={form.category} onChange={(e) => set("category", e.target.value)} />
                  </FormField>
                  <FormField label="Birim">
                    <input data-testid="stock-form-unit" placeholder="çuval, kg, lt..." value={form.unit} onChange={(e) => set("unit", e.target.value)} />
                  </FormField>
                </div>
              </>
            )}
            <div className="form-row">
              <FormField label="Miktar">
                <input required type="number" step="0.1" data-testid="stock-form-qty" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} />
              </FormField>
              <FormField label="Minimum uyarı">
                <input type="number" step="0.1" data-testid="stock-form-min" value={form.min_quantity} onChange={(e) => set("min_quantity", e.target.value)} />
              </FormField>
            </div>
            <FormField label="Not">
              <textarea data-testid="stock-form-note" value={form.note} onChange={(e) => set("note", e.target.value)} />
            </FormField>
            <SubmitButton testId="stock-form-submit">{editing ? "Güncelle" : "Kaydet"}</SubmitButton>
          </form>
        </Modal>
      )}
    </>
  );
}
